import { forwardRef } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/utils/cn';
import { tokens } from '@/theme/tokens';
import type { ButtonProps } from './Button.types';

/**
 * # Button
 *
 * Zakladni interaktivni prvek pro akce.
 * Podporuje varianty, velikosti, ikony a loading stav.
 *
 * @example
 * <Button variant="primary" onClick={save}>Ulozit</Button>
 * <Button variant="outline" loading>Nacitani...</Button>
 * <Button leftIcon={<Plus />} size="sm">Pridat</Button>
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      variant = 'primary',
      size = 'md',
      leftIcon,
      rightIcon,
      loading = false,
      fullWidth = false,
      disabled,
      className,
      as: Component = 'button',
      type = 'button',
      title,
      'aria-label': ariaLabel,
      'data-testid': testId,
      ...props
    },
    ref,
  ) => {
    const maTextovyObsah = children !== undefined && children !== null && children !== false;
    const dostupnyPopis = ariaLabel || (!maTextovyObsah && typeof title === 'string' ? title : undefined);

    return (
      <Component
        ref={ref}
        type={Component === 'button' ? type : undefined}
        disabled={disabled || loading}
        data-testid={testId}
        title={title}
        aria-label={dostupnyPopis}
        className={cn(
          // Base
          'inline-flex items-center justify-center font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-zinc-900 disabled:opacity-50 disabled:pointer-events-none disabled:cursor-not-allowed',
          tokens.transition.fast,
          tokens.radius.sm,

          // Sizes
          {
            'px-3 py-1.5 text-sm': size === 'sm',
            'px-4 py-2 text-sm': size === 'md',
            'px-6 py-3 text-base': size === 'lg',
          },

          // Variants
          {
            // Primary
            'bg-primary-600 text-white hover:bg-primary-700 focus:ring-primary-500/30 dark:bg-primary-500 dark:hover:bg-primary-600':
              variant === 'primary',

            // Secondary
            'bg-gray-100 text-gray-800 hover:bg-gray-200 focus:ring-gray-400/30 dark:bg-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-700':
              variant === 'secondary',

            // Danger
            'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500/30 dark:bg-red-500 dark:hover:bg-red-600':
              variant === 'danger',

            // Success
            'bg-emerald-600 text-white hover:bg-emerald-700 focus:ring-emerald-500/30 dark:bg-emerald-500 dark:hover:bg-emerald-600':
              variant === 'success',

            // Warning
            'bg-amber-500 text-white hover:bg-amber-600 focus:ring-amber-500/30 dark:bg-amber-500 dark:hover:bg-amber-600':
              variant === 'warning',

            // Info
            'bg-cyan-500 text-white hover:bg-cyan-600 focus:ring-cyan-500/30': variant === 'info',

            // Gradient
            'bg-gradient-to-r from-emerald-600 via-teal-500 to-cyan-500 hover:from-emerald-700 hover:via-teal-600 hover:to-cyan-600 text-white font-bold border-0 shadow-[0_14px_32px_rgba(13,148,136,0.25)] hover:shadow-[0_18px_36px_rgba(13,148,136,0.32)] hover:-translate-y-0.5 focus:ring-emerald-500/30':
              variant === 'gradient',

            // Ghost
            'bg-transparent text-gray-700 hover:bg-gray-100 focus:ring-gray-400/20 dark:text-zinc-300 dark:hover:bg-white/5':
              variant === 'ghost',

            // Outline
            'bg-transparent border border-gray-300 text-gray-700 hover:bg-gray-50 focus:ring-primary-500/20 dark:border-zinc-600 dark:text-zinc-300 dark:hover:bg-white/5':
              variant === 'outline',
          },

          // Width
          fullWidth && 'w-full',

          className,
        )}
        {...props}
      >
        {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
        {!loading && leftIcon && <span className="mr-2">{leftIcon}</span>}
        {children}
        {!loading && rightIcon && <span className="ml-2">{rightIcon}</span>}
      </Component>
    );
  },
);

Button.displayName = 'Button';
